import React, { useContext, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Input, Label, Button, FormFeedback } from 'reactstrap';
import { getRazorpayCredentials, createRazorpayCredentials, updateRazorpayCredentials } from '../../api/api';
import handleUnauthorized401MutateError from '../../utils/handleUnauthorized401MutateError';
import AuthContext from '../../context/AuthContext';
import Loader from '../../components/Loader';

function PaymentSettingsTab() {
    const { refreshToken } = useContext(AuthContext);
    const queryClient = useQueryClient();

    // Existing credentials state
    const [credentialId, setCredentialId] = useState(null);
    const [maskedKeyId, setMaskedKeyId] = useState(null);
    const [isActive, setIsActive] = useState(true);
    const [hasCredentials, setHasCredentials] = useState(false);

    // Form fields — always write-only inputs
    const [form, setForm] = useState({ key_id: '', key_secret: '', webhook_secret: '' });
    const [showSecret, setShowSecret] = useState(false);
    const [showWebhook, setShowWebhook] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    const { refetch, isLoading: loadingCreds } = useQuery({
        queryKey: ['razorpay-credentials'],
        queryFn: getRazorpayCredentials,
        retry: false,
        onSuccess: (data) => {
            const results = data?.results || data;
            if (Array.isArray(results) && results.length > 0) {
                const cred = results[0];
                setCredentialId(cred.id);
                setMaskedKeyId(cred.masked_key_id);
                setIsActive(cred.is_active);
                setHasCredentials(true);
            } else {
                setHasCredentials(false);
            }
        },
        onError: (err) => {
            handleUnauthorized401MutateError(err, refreshToken, refetch);
        },
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        if (fieldErrors[name]) {
            setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
        }
    };

    const onMutateError = (err) => {
        const data = err?.response?.data;
        if (data && typeof data === 'object' && !data.detail) {
            const errors = {};
            Object.keys(data).forEach((key) => {
                errors[key] = Array.isArray(data[key]) ? data[key][0] : data[key];
            });
            setFieldErrors(errors);
            toast.error(Object.values(errors)[0]);
        } else {
            toast.error(`An error occurred! ${data?.detail || 'Unknown error'}`);
        }
    };

    const createMutation = useMutation(createRazorpayCredentials, {
        onSuccess: (data) => {
            setFieldErrors({});
            setMaskedKeyId(data.masked_key_id);
            setCredentialId(data.id);
            setIsActive(data.is_active);
            setHasCredentials(true);
            setForm({ key_id: '', key_secret: '', webhook_secret: '' });
            toast.success('Razorpay credentials saved successfully!');
            queryClient.invalidateQueries(['razorpay-credentials']);
        },
        onError: onMutateError,
    });

    const updateMutation = useMutation(updateRazorpayCredentials, {
        onSuccess: (data) => {
            setFieldErrors({});
            setMaskedKeyId(data.masked_key_id);
            setIsActive(data.is_active);
            setForm({ key_id: '', key_secret: '', webhook_secret: '' });
            toast.success('Razorpay credentials updated successfully!');
            queryClient.invalidateQueries(['razorpay-credentials']);
        },
        onError: onMutateError,
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = { is_active: isActive };
        if (form.key_id) payload.key_id = form.key_id;
        if (form.key_secret) payload.key_secret = form.key_secret;
        if (form.webhook_secret) payload.webhook_secret = form.webhook_secret;

        if (hasCredentials) {
            updateMutation.mutate({ id: credentialId, formData: payload });
        } else {
            if (!form.key_id || !form.key_secret) {
                setFieldErrors({
                    ...(!form.key_id && { key_id: 'Key ID is required.' }),
                    ...(!form.key_secret && { key_secret: 'Key Secret is required.' }),
                });
                toast.error('Please fill in Key ID and Key Secret.');
                return;
            }
            createMutation.mutate(payload);
        }
    };

    const isSaving = createMutation.isLoading || updateMutation.isLoading;

    const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/event-listener/`;

    return (
        <div className="row">
            <div className="settings-card ml-3">
                <div className="card-box">

                    {/* ── Status banner ── */}
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <h6
                            className="text-muted mb-0"
                            style={{ textTransform: 'uppercase', fontSize: 11, letterSpacing: 1 }}
                        >
                            Razorpay Account
                        </h6>
                        {loadingCreds ? (
                            <span className="badge badge-secondary">Checking...</span>
                        ) : hasCredentials ? (
                            <span
                                className="badge"
                                style={{
                                    backgroundColor: isActive ? 'rgba(26,188,156,0.15)' : 'rgba(245,50,92,0.15)',
                                    color: isActive ? '#1abc9c' : '#f5325c',
                                    fontWeight: 600,
                                    fontSize: 11,
                                    padding: '4px 10px',
                                    borderRadius: 4,
                                }}
                            >
                                {isActive ? '● Connected' : '● Disconnected'}
                            </span>
                        ) : (
                            <span
                                className="badge"
                                style={{
                                    backgroundColor: 'rgba(108,117,125,0.15)',
                                    color: '#6e768e',
                                    fontWeight: 600,
                                    fontSize: 11,
                                    padding: '4px 10px',
                                    borderRadius: 4,
                                }}
                            >
                                ○ Not configured
                            </span>
                        )}
                    </div>

                    {/* ── Current key display ── */}
                    {hasCredentials && maskedKeyId && (
                        <div
                            className="mb-4 p-3"
                            style={{
                                backgroundColor: 'rgba(77,91,237,0.07)',
                                border: '1px solid rgba(77,91,237,0.2)',
                                borderRadius: 6,
                            }}
                        >
                            <div style={{ fontSize: 12, color: '#9097a7', marginBottom: 4 }}>Current Key ID</div>
                            <code style={{ fontSize: 13, color: '#f1f5f7', letterSpacing: 1 }}>{maskedKeyId}</code>
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>

                        {/* ── API Credentials ── */}
                        <h6
                            className="text-muted mb-3"
                            style={{ textTransform: 'uppercase', fontSize: 11, letterSpacing: 1 }}
                        >
                            API Credentials
                        </h6>
                        <small className="text-muted d-block mb-3">
                            Get these from your Razorpay Dashboard → Settings → API Keys.{' '}
                            {hasCredentials && 'Leave fields blank to keep existing values.'}
                        </small>

                        <div className="form-group">
                            <Label>
                                Key ID{' '}
                                {!hasCredentials && <span style={{ color: '#f5325c' }}>*</span>}
                            </Label>
                            <Input
                                type="text"
                                name="key_id"
                                className="form-control"
                                value={form.key_id}
                                onChange={handleChange}
                                placeholder={hasCredentials ? 'Enter new Key ID to replace existing' : 'rzp_live_xxxxxxxxxxxx'}
                                autoComplete="off"
                                invalid={!!fieldErrors.key_id}
                            />
                            {fieldErrors.key_id
                                ? <FormFeedback>{fieldErrors.key_id}</FormFeedback>
                                : <small className="text-muted">Starts with rzp_live_ (production) or rzp_test_ (test mode).</small>
                            }
                        </div>

                        <div className="form-group">
                            <Label>
                                Key Secret{' '}
                                {!hasCredentials && <span style={{ color: '#f5325c' }}>*</span>}
                            </Label>
                            <div style={{ position: 'relative' }}>
                                <Input
                                    type={showSecret ? 'text' : 'password'}
                                    name="key_secret"
                                    className="form-control"
                                    value={form.key_secret}
                                    onChange={handleChange}
                                    placeholder={hasCredentials ? 'Enter new Key Secret to replace existing' : 'Your Razorpay Key Secret'}
                                    autoComplete="new-password"
                                    invalid={!!fieldErrors.key_secret}
                                    style={{ paddingRight: 40 }}
                                />
                                <span
                                    onClick={() => setShowSecret((v) => !v)}
                                    style={{
                                        position: 'absolute', right: 10, top: '50%',
                                        transform: 'translateY(-50%)', cursor: 'pointer',
                                        color: '#6e768e', fontSize: 16, zIndex: 5,
                                    }}
                                >
                                    <i className={`mdi ${showSecret ? 'mdi-eye-off' : 'mdi-eye'}`} />
                                </span>
                            </div>
                            {fieldErrors.key_secret
                                ? <FormFeedback className="d-block">{fieldErrors.key_secret}</FormFeedback>
                                : <small className="text-muted">Stored encrypted. Never shown after saving.</small>
                            }
                        </div>

                        <hr style={{ borderColor: '#333', margin: '24px 0' }} />

                        {/* ── Webhook ── */}
                        <h6
                            className="text-muted mb-3"
                            style={{ textTransform: 'uppercase', fontSize: 11, letterSpacing: 1 }}
                        >
                            Webhook Configuration
                        </h6>

                        <div
                            className="mb-3 p-3"
                            style={{
                                backgroundColor: 'rgba(255,193,7,0.07)',
                                border: '1px solid rgba(255,193,7,0.2)',
                                borderRadius: 6,
                            }}
                        >
                            <div style={{ fontSize: 12, color: '#9097a7', marginBottom: 6 }}>
                                <i className="mdi mdi-information-outline mr-1" />
                                Setup Instructions
                            </div>
                            <ol style={{ fontSize: 12, color: '#9097a7', marginBottom: 0, paddingLeft: 16 }}>
                                <li>Go to your <strong style={{ color: '#f1f5f7' }}>Razorpay Dashboard → Settings → Webhooks</strong></li>
                                <li>Add a new webhook with the URL below</li>
                                <li>Enable events: <code style={{ color: '#4d5bed' }}>payment.captured</code>, <code style={{ color: '#4d5bed' }}>payment.failed</code>, <code style={{ color: '#4d5bed' }}>refund.created</code>, <code style={{ color: '#4d5bed' }}>refund.processed</code>, <code style={{ color: '#4d5bed' }}>refund.failed</code></li>
                                <li>Copy the generated <strong style={{ color: '#f1f5f7' }}>Webhook Secret</strong> and paste it below</li>
                            </ol>
                        </div>

                        <div className="form-group">
                            <Label>Webhook URL</Label>
                            <div className="input-group">
                                <Input
                                    type="text"
                                    className="form-control"
                                    value={webhookUrl}
                                    readOnly
                                    style={{ fontFamily: 'monospace', fontSize: 12 }}
                                />
                                <div className="input-group-append">
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-sm"
                                        onClick={() => {
                                            navigator.clipboard.writeText(webhookUrl);
                                            toast.info('Webhook URL copied!');
                                        }}
                                    >
                                        <i className="mdi mdi-content-copy" />
                                    </button>
                                </div>
                            </div>
                            <small className="text-muted">Paste this URL in your Razorpay Webhook settings.</small>
                        </div>

                        <div className="form-group">
                            <Label>Webhook Secret</Label>
                            <div style={{ position: 'relative' }}>
                                <Input
                                    type={showWebhook ? 'text' : 'password'}
                                    name="webhook_secret"
                                    className="form-control"
                                    value={form.webhook_secret}
                                    onChange={handleChange}
                                    placeholder={hasCredentials ? 'Enter new Webhook Secret to replace existing' : 'Paste Webhook Secret from Razorpay'}
                                    autoComplete="new-password"
                                    invalid={!!fieldErrors.webhook_secret}
                                    style={{ paddingRight: 40 }}
                                />
                                <span
                                    onClick={() => setShowWebhook((v) => !v)}
                                    style={{
                                        position: 'absolute', right: 10, top: '50%',
                                        transform: 'translateY(-50%)', cursor: 'pointer',
                                        color: '#6e768e', fontSize: 16, zIndex: 5,
                                    }}
                                >
                                    <i className={`mdi ${showWebhook ? 'mdi-eye-off' : 'mdi-eye'}`} />
                                </span>
                            </div>
                            {fieldErrors.webhook_secret
                                ? <FormFeedback className="d-block">{fieldErrors.webhook_secret}</FormFeedback>
                                : <small className="text-muted">Used to verify webhook authenticity. Stored encrypted.</small>
                            }
                        </div>

                        <hr style={{ borderColor: '#333', margin: '24px 0' }} />

                        {/* ── Active toggle ── */}
                        {hasCredentials && (
                            <div className="form-group">
                                <div className="d-flex align-items-center" style={{ gap: 12 }}>
                                    <Label className="mb-0">Enable Razorpay Payments</Label>
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={isActive}
                                            onChange={(e) => setIsActive(e.target.checked)}
                                            style={{ cursor: 'pointer' }}
                                        />
                                    </div>
                                </div>
                                <small className="text-muted">
                                    Disable to temporarily stop Razorpay on your store without removing credentials.
                                </small>
                            </div>
                        )}

                        <Button
                            type="submit"
                            className="btn bx-btn-primary waves-effect waves-light"
                            disabled={isSaving}
                        >
                            {isSaving
                                ? <><Loader size="small" />Saving...</>
                                : hasCredentials ? 'Update' : 'Save'
                            }
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default PaymentSettingsTab;
